import type { CardIdentity, BatchGridBulkIdentityProposal } from '@tcvs/shared';

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const scalar = (value: unknown) =>
  value === null ||
  typeof value === 'string' ||
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value));
function identity(value: unknown): boolean {
  return (
    record(value) &&
    ['category', 'set', 'number', 'name'].every((key) => typeof value[key] === 'string') &&
    Object.values(value).every(scalar)
  );
}
export function validChecklistIdentityProposal(
  value: unknown,
): value is BatchGridBulkIdentityProposal {
  return (
    record(value) &&
    (value.beforeCardIdentity === null || identity(value.beforeCardIdentity)) &&
    (value.beforeDraftIdentity === null || identity(value.beforeDraftIdentity)) &&
    identity(value.proposedIdentity) &&
    Array.isArray(value.changes) &&
    value.changes.every(
      (change) =>
        record(change) &&
        typeof change.field === 'string' &&
        scalar(change.before) &&
        scalar(change.after),
    )
  );
}
const labels: Record<string, string> = {
  category: 'Category',
  set: 'Set',
  number: 'Printed card number',
  name: 'Player / card name',
  year: 'Year',
  brand: 'Brand',
  subset: 'Subset',
  variation: 'Treatment / parallel',
  serialNumber: 'Observed serial number',
  serialTo: 'Print run',
  serialNumbered: 'Serial numbered',
  rookie: 'Rookie',
  autograph: 'Autograph',
  memorabilia: 'Memorabilia',
  team: 'Team',
  language: 'Language',
  attributes: 'Attributes',
};
const text = (value: unknown) =>
  value === undefined
    ? 'Not recorded (removed if previously present)'
    : value === null
      ? 'Empty (null)'
      : typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : value === ''
          ? 'Empty text'
          : String(value);
/** Full independently observed Card/draft preimages, including removals absent from row-composition diffs. */
export function ChecklistSelectionChanges({
  beforeCard,
  beforeDraft,
  proposed,
  beforeLocal,
}: {
  beforeCard: CardIdentity | null;
  beforeDraft: CardIdentity | null;
  proposed: CardIdentity;
  beforeLocal?: CardIdentity;
}) {
  const keys = new Set([
    ...Object.keys(beforeCard ?? {}),
    ...Object.keys(beforeDraft ?? {}),
    ...Object.keys(proposed),
    ...Object.keys(beforeLocal ?? {}),
  ]);
  return (
    <section aria-label="Checklist identity changes">
      <p>
        Review every identity field before accepting this choice. Saved card and saved review draft
        are shown separately. This does not approve photos, condition or a listing.
      </p>
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Saved card identity{beforeCard === null ? ' (not recorded)' : ''}</th>
            <th>Saved review draft{beforeDraft === null ? ' (not recorded)' : ''}</th>
            {beforeLocal ? <th>Current local review</th> : null}
            <th>Proposed identity</th>
          </tr>
        </thead>
        <tbody>
          {[...keys].sort().map((key) => (
            <tr key={key}>
              <th scope="row">{labels[key] ?? key}</th>
              {[beforeCard, beforeDraft, ...(beforeLocal ? [beforeLocal] : []), proposed].map(
                (item, index) => (
                  <td key={index} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {text(item?.[key as keyof CardIdentity])}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
